import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/services/apiClient';
import { SupplierPoRows, qtyOf, OnTime } from './SupplierPoRows';

const ago = (days) => { const d = new Date(Date.now() - days * 864e5); return d.toISOString().slice(0, 10); };
const Stat = ({ label, value, testId, tone = 'text-gray-900' }) => (
  <div className="rounded-lg border bg-white px-4 py-2"><p className="text-[11px] text-gray-500">{label}</p><p className={`text-lg font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</p></div>
);

/** Per supplier, for the POs raised in the period: on-time delivery (weighted by qty), pending qty, late and overdue POs. */
export const SupplierKpis = ({ sections }) => {
  const [f, setF] = useState({ date_from: ago(180), date_to: '', section: 'all', q: '' });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const load = useCallback(() => {
    setData(null);
    const params = { ...f, section: f.section === 'all' ? '' : f.section };
    apiClient.get('/po-followup/suppliers', { params }).then((r) => setData(r.data)).catch(() => setData({ suppliers: [], summary: null }));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const s = data?.summary;
  return (
    <div className="space-y-3" data-testid="supplier-kpis">
      <div className="flex flex-wrap items-end gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input value={f.q} onChange={(e) => set('q')(e.target.value)} placeholder="Search supplier" className="h-10 pl-9" data-testid="kpi-search" />
        </div>
        <Select value={f.section} onValueChange={set('section')}>
          <SelectTrigger className="h-10 w-44" data-testid="kpi-section"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sections</SelectItem>{sections.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
        </Select>
        <label className="text-xs text-gray-500">PO date from<Input type="date" value={f.date_from} onChange={(e) => set('date_from')(e.target.value)} className="h-10 w-40" data-testid="kpi-from" /></label>
        <label className="text-xs text-gray-500">to<Input type="date" value={f.date_to} onChange={(e) => set('date_to')(e.target.value)} className="h-10 w-40" data-testid="kpi-to" /></label>
      </div>
      {s && <div className="flex flex-wrap gap-2">
        <Stat label="Suppliers" value={s.suppliers} testId="kpi-sum-suppliers" />
        <Stat label="POs" value={s.po_count} testId="kpi-sum-pos" />
        <Stat label="On-time (by qty)" value={s.on_time_pct == null ? '—' : `${s.on_time_pct}%`} testId="kpi-sum-ontime" tone="text-emerald-700" />
        <Stat label="Late POs" value={s.late_po_count} testId="kpi-sum-late" tone="text-rose-700" />
        <Stat label="Overdue now" value={s.overdue_po_count} testId="kpi-sum-overdue" tone="text-amber-700" />
      </div>}
      <p className="text-[11px] text-gray-500">On-time = share of the ordered qty received by each PO's expected delivery date, weighted by qty. A PO line counts once its date has passed or it is fully received.</p>
      {!data ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-gray-400" />
        : !data.suppliers.length ? <p className="py-10 text-center text-sm text-gray-500" data-testid="kpi-empty">No Purchase Orders in this period.</p> : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600"><tr>
                <th className="p-2 text-left">Supplier</th><th className="p-2 text-right">POs</th><th className="p-2 text-right">Ordered</th>
                <th className="p-2 text-right">Received</th><th className="p-2 text-right">Pending</th><th className="p-2 text-left">On-time (by qty)</th>
                <th className="p-2 text-right">Late POs</th><th className="p-2 text-right">Overdue now</th><th className="p-2 text-right">Avg days late</th></tr></thead>
              <tbody>
                {data.suppliers.map((v, i) => { const k = v.vendor_name.toLowerCase(); return (
                  <React.Fragment key={k}>
                    <tr className="cursor-pointer border-t hover:bg-gray-50" onClick={() => setOpen((o) => ({ ...o, [k]: !o[k] }))} data-testid={`kpi-row-${i}`}>
                      <td className="p-2 font-medium">{open[k] ? <ChevronDown className="mr-1 inline h-4 w-4" /> : <ChevronRight className="mr-1 inline h-4 w-4" />}{v.vendor_name}</td>
                      <td className="p-2 text-right tabular-nums">{v.po_count}</td>
                      <td className="p-2 text-right text-xs tabular-nums">{qtyOf(v.ordered)}</td>
                      <td className="p-2 text-right text-xs tabular-nums">{qtyOf(v.received)}</td>
                      <td className="p-2 text-right text-xs font-semibold text-amber-700 tabular-nums" data-testid={`kpi-pending-${i}`}>{qtyOf(v.pending)}</td>
                      <td className="p-2"><OnTime pct={v.on_time_pct} testId={`kpi-ontime-${i}`} /></td>
                      <td className="p-2 text-right tabular-nums text-rose-700" data-testid={`kpi-late-${i}`}>{v.late_po_count}</td>
                      <td className="p-2 text-right tabular-nums text-amber-700" data-testid={`kpi-overdue-${i}`}>{v.overdue_po_count}</td>
                      <td className="p-2 text-right tabular-nums">{v.avg_days_late ?? '—'}</td>
                    </tr>
                    {open[k] && <tr><td colSpan={9} className="bg-gray-50 p-2"><SupplierPoRows pos={v.pos} testId={`kpi-pos-${i}`} /></td></tr>}
                  </React.Fragment>); })}
              </tbody>
            </table>
          </div>)}
    </div>
  );
};

export default SupplierKpis;
